
import { useState, useEffect } from 'react';
import Logo from '../../components/Logo';

export default function HomePage() {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  const [eventsNotificationEmail, setEventsNotificationEmail] = useState('');
  const [eventsNotificationStatus, setEventsNotificationStatus] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBookingClick = (serviceName: string) => {
    setSelectedService(serviceName);
    setIsBookingModalOpen(true);
    setSubmitStatus('idle');
  };

  const handleNotificationClick = () => {
    setSelectedService('Events & Workshops - Notification');
    setIsBookingModalOpen(true);
    setSubmitStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5ju8mqgbpr25eot4cdg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setTimeout(() => {
          setIsBookingModalOpen(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5kldnpubuu1ag7lek8g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('Thank you for subscribing!');
        form.reset();
      } else {
        setFormStatus('Something went wrong. Please try again.');
      }
    } catch (error) {
      setFormStatus('Something went wrong. Please try again.');
    }
  };

  const handleEventsNotificationSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEventsNotificationStatus('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/events-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setEventsNotificationStatus('Thank you! You\'ll be notified of upcoming events.');
        form.reset();
        setEventsNotificationEmail('');
      } else {
        setEventsNotificationStatus('Something went wrong. Please try again.');
      }
    } catch (error) {
      setEventsNotificationStatus('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EFE7]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="w-12 h-12" color={isScrolled ? '#6B1F3D' : '#F5EFE7'} />
            <span className={`text-2xl font-['Great_Vibes'] ${isScrolled ? 'text-[#6B1F3D]' : 'text-[#F5EFE7]'}`}>
              Sacred Love
            </span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#about" className={`${isScrolled ? 'text-[#6B1F3D]' : 'text-[#F5EFE7]'} hover:opacity-70 transition-opacity cursor-pointer font-['Lato']`}>About</a>
            <a href="#events" className={`${isScrolled ? 'text-[#6B1F3D]' : 'text-[#F5EFE7]'} hover:opacity-70 transition-opacity cursor-pointer font-['Lato']`}>Services</a>
            <a href="#contact" className={`px-6 py-2 rounded-full border-2 ${isScrolled ? 'border-[#6B1F3D] text-[#6B1F3D] hover:bg-[#6B1F3D] hover:text-white' : 'border-[#F5EFE7] text-[#F5EFE7] hover:bg-[#F5EFE7] hover:text-[#6B1F3D]'} transition-all cursor-pointer whitespace-nowrap font-['Lato']`}>
              Book Session
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://public.readdy.ai/ai/img_res/c618dbc52e52d6e087476d0d152234c8.jpg" 
            alt="Sacred Love Hero"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#6B1F3D]/40 via-[#6B1F3D]/20 to-[#6B1F3D]/30"></div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl w-full">
          <h1 className="text-6xl sm:text-8xl md:text-9xl lg:text-[12rem] font-['Great_Vibes'] text-[#F5EFE7] font-bold mb-8 md:mb-12 leading-none">
            Sacred Love
          </h1>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-serif text-[#F5EFE7] mb-4 md:mb-6 leading-tight" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Awaken Your Sacred Essence
          </h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-[#F5EFE7]/90 mb-8 md:mb-12 font-light leading-relaxed max-w-2xl mx-auto">
            Journey into the depths of spiritual connection and discover the divine love that resides within your soul
          </p>
          <a href="#about" className="inline-flex items-center gap-3 bg-[#F5EFE7] text-[#6B1F3D] px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg hover:bg-white transition-all shadow-lg whitespace-nowrap cursor-pointer">
            <span>Begin Your Journey</span>
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>

      {/* About Me Section */}
      <section id="about" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="rounded-[32px] overflow-hidden shadow-xl relative h-[700px] w-full bg-[#F5EFE7] flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/49a56f973024f64ed0e5539001dfaf2d/5bf8f2845438c02578422dcda68fcf9c.jpeg" 
                  alt="Sacred Love"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            
            <div>
              <p className="text-[#6B1F3D] font-['Lato'] text-xl mb-4 uppercase tracking-wider">About Sacred Love</p>
              <h3 className="text-5xl font-serif text-[#6B1F3D] mb-6 leading-tight">
                Nurturing Souls Through Sacred Connection
              </h3>
              
              <div className="space-y-4 mb-6">
                <p className="text-lg text-gray-700 leading-relaxed font-['Lato']">
                  Rooted in spirituality, embodied practice, and conscious relating Sacred Love offers experiences that invite people to meet themselves and one another with honesty, presence, and care. Through ritual, live music, movement, and facilitated connection Sacred Love creates containers where intimacy can be explored safely, playfully, and with integrity.
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed font-['Lato']">
                  At its heart, Sacred Love is about liberation- from shame, from silence, and from the inherited stories that limit our capacity to love, desire, and relate freely. It is a space where shadow is welcomed as part of wholeness, where boundaries are honoured and where consent is understood an art of conscious communication and a practice of self-trust.
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed font-['Lato']">
                  Sacred Love is led by Samphire Hayes - an ISTA graduate and qualified psychotherapist specialising in trauma, sexuality, and the body. Samphire brings together clinical expertise, somatic awareness, and years of experience in relational work and navigating ceremonial and sacred sexuality space.
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed font-['Lato']">
                  Alongside group offerings Sacred Love also provides one-to-one integration and couples coaching supporting individuals and partners to weave insights from these experiences into their everyday lives and relationships.
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed font-['Lato']">
                  Sacred Love is for those who are curious about deeper intimacy with themselves, and others and explore love, desire, and connection as sacred, embodied and alive.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="events" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#6B1F3D] uppercase tracking-[0.2em] text-sm font-light mb-4 font-lato">SACRED OFFERINGS</p>
            <h2 className="text-5xl font-light mb-6 font-cormorant text-[#2C1810]">Services</h2>
            <p className="text-lg text-[#5C4A42] max-w-2xl mx-auto font-lato">
              Explore pathways to deeper connection, intimacy, and self-discovery through guided experiences
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Events & Workshops */}
            <div className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=sacred%20intimate%20gathering%20with%20candles%20soft%20lighting%20people%20in%20circle%20holding%20hands%20warm%20atmosphere%20spiritual%20workshop%20setting%20gentle%20colors%20peaceful%20energy%20mindful%20connection%20ritual%20space&width=600&height=400&seq=service1&orientation=landscape"
                  alt="Events & Workshops"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8 bg-white">
                <h3 className="text-2xl font-light mb-3 font-cormorant text-[#2C1810]">Events & Workshops</h3>
                <p className="text-[#5C4A42] mb-6 font-lato leading-relaxed">
                  Join group experiences that weave together ritual, music, and facilitated connection. These gatherings create sacred space for exploration, vulnerability, and authentic relating.
                </p>
                <form id="events-notification-form" data-readdy-form onSubmit={handleEventsNotificationSubmit} className="space-y-3">
                  <input 
                    type="email"
                    name="email"
                    value={eventsNotificationEmail}
                    onChange={(e) => setEventsNotificationEmail(e.target.value)}
                    placeholder="Your email address"
                    className="w-full px-4 py-2 border border-[#D4C4B0] rounded-lg focus:outline-none focus:border-[#6B1F3D] text-sm font-lato"
                    required
                  />
                  <button 
                    type="submit"
                    className="bg-[#6B1F3D] text-[#F5EFE7] px-6 py-2 rounded-full hover:bg-[#8B2F4D] transition-all whitespace-nowrap cursor-pointer w-full"
                  >
                    Get Notifications of Next Events
                  </button>
                  {eventsNotificationStatus && (
                    <p className="text-sm text-[#6B1F3D] font-lato">{eventsNotificationStatus}</p>
                  )}
                </form>
              </div>
            </div>

            {/* Temple Integration */}
            <div className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=peaceful%20one-on-one%20counseling%20session%20soft%20natural%20light%20comfortable%20intimate%20space%20gentle%20guidance%20sacred%20temple%20integration%20therapy%20setting%20warm%20tones%20calming%20atmosphere%20mindful%20presence&width=600&height=400&seq=service2&orientation=landscape"
                  alt="Temple Integration"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8 bg-white">
                <h3 className="text-2xl font-light mb-3 font-cormorant text-[#2C1810]">Temple Integration</h3>
                <p className="text-[#5C4A42] mb-6 font-lato leading-relaxed">
                  One-to-one sessions designed to help you integrate and make sense of temple experiences. A safe container for processing, reflection, and embodying insights.
                </p>
                <button 
                  onClick={() => handleBookingClick('Temple Integration')}
                  className="bg-[#6B1F3D] text-[#F5EFE7] px-6 py-2 rounded-full hover:bg-[#8B2F4D] transition-all whitespace-nowrap cursor-pointer w-full"
                >
                  Book Session
                </button>
              </div>
            </div>

            {/* Couples Coaching */}
            <div className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=loving%20couple%20sitting%20together%20in%20peaceful%20counseling%20space%20soft%20warm%20lighting%20intimate%20connection%20relationship%20coaching%20setting%20gentle%20atmosphere%20supportive%20environment%20mindful%20presence%20together&width=600&height=400&seq=service3&orientation=landscape"
                  alt="Couples Coaching"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8 bg-white">
                <h3 className="text-2xl font-light mb-3 font-cormorant text-[#2C1810]">Couples Coaching</h3>
                <p className="text-[#5C4A42] mb-6 font-lato leading-relaxed">
                  Relational support for partners exploring deeper intimacy, communication, and sacred sexuality. Navigate challenges and celebrate growth together.
                </p>
                <button 
                  onClick={() => handleBookingClick('Couples Coaching')}
                  className="bg-[#6B1F3D] text-[#F5EFE7] px-6 py-2 rounded-full hover:bg-[#8B2F4D] transition-all whitespace-nowrap cursor-pointer w-full"
                >
                  Book Session
                </button>
              </div>
            </div>

            {/* Sexuality & Kink Exploration */}
            <div className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=private%20confidential%20therapy%20session%20safe%20non-judgmental%20space%20comfortable%20intimate%20setting%20soft%20lighting%20supportive%20atmosphere%20personal%20exploration%20counseling%20room%20warm%20welcoming%20environment%20mindful%20listening&width=600&height=400&seq=service4&orientation=landscape"
                  alt="Sexuality & Kink Exploration Sessions"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8 bg-white">
                <h3 className="text-2xl font-light mb-3 font-cormorant text-[#2C1810]">Sexuality & Kink Exploration Sessions</h3>
                <p className="text-[#5C4A42] mb-6 font-lato leading-relaxed">
                  Private, one-to-one sessions for disussing your desires and curiosities in a non-judgmental space. Compassionate guidance for your unique journey.
                </p>
                <button 
                  onClick={() => handleBookingClick('Sexuality & Kink Exploration')}
                  className="bg-[#6B1F3D] text-[#F5EFE7] px-6 py-2 rounded-full hover:bg-[#8B2F4D] transition-all whitespace-nowrap cursor-pointer w-full"
                >
                  Book Session
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#6B1F3D] text-[#F5EFE7] pt-20 pb-8 px-6 rounded-t-[40px]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            {/* Newsletter */}
            <div>
              <h4 className="text-5xl font-serif mb-4">Stay Connected</h4>
              <p className="text-[#F5EFE7]/80 mb-4 font-['Lato']">
                Receive sacred wisdom, event updates, and spiritual insights delivered to your inbox
              </p>
              <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-4">
                <input 
                  type="email"
                  name="email"
                  placeholder="Your email address"
                  className="w-full bg-transparent border-b-2 border-[#F5EFE7] text-[#F5EFE7] placeholder-[#F5EFE7]/50 py-3 focus:outline-none focus:border-white transition-colors font-['Lato']"
                  required
                />
                <button 
                  type="submit"
                  className="flex items-center gap-2 bg-[#F5EFE7] text-[#6B1F3D] px-6 py-3 rounded-full hover:bg-white transition-all whitespace-nowrap cursor-pointer"
                >
                  <span>Subscribe</span>
                  <i className="ri-sparkling-2-line"></i>
                </button>
                {formStatus && (
                  <p className="text-[#F5EFE7] text-sm mt-2">{formStatus}</p>
                )}
              </form>
            </div>
            
            {/* Explore */}
            <div>
              <h5 className="text-sm uppercase tracking-wider mb-6 text-[#F5EFE7]/90">Explore</h5>
              <div className="space-y-4">
                <a href="#about" className="block text-[#F5EFE7] hover:text-white transition-colors border-b border-transparent hover:border-[#D4AF37] pb-1 w-fit cursor-pointer">About</a>
                <a href="#events" className="block text-[#F5EFE7] hover:text-white transition-colors border-b border-transparent hover:border-[#D4AF37] pb-1 w-fit cursor-pointer">Services</a>
                <a href="#contact" className="block text-[#F5EFE7] hover:text-white transition-colors border-b border-transparent hover:border-[#D4AF37] pb-1 w-fit cursor-pointer">Sessions</a>
              </div>
            </div>
            
            {/* Connect */}
            <div>
              <h5 className="text-sm uppercase tracking-wider mb-6 text-[#F5EFE7]/90">Connect</h5>
              <div className="space-y-3 mb-8">
                <a href="mailto:hello@sacredlove.uk" className="block text-lg border-b border-[#F5EFE7] pb-1 w-fit hover:border-white transition-colors cursor-pointer font-['Lato']">hello@sacredlove.uk</a>
                <a 
                  href="https://instagram.com/sacredlove.uk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#F5EFE7] hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-line text-xl"></i>
                  <span className="font-['Lato']">@sacredlove.uk</span>
                </a>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="border-t border-[#F5EFE7]/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#F5EFE7]/70 text-sm font-['Lato']">© 2025 Sacred Love. All rights reserved.</p>
            
            <div className="flex gap-6">
              <a href="#privacy" className="text-[#F5EFE7] hover:text-white border-b border-transparent hover:border-[#D4AF37] pb-1 transition-colors cursor-pointer text-sm">Privacy Policy</a>
              <a href="#terms" className="text-[#F5EFE7] hover:text-white border-b border-transparent hover:border-[#D4AF37] pb-1 transition-colors cursor-pointer text-sm">Terms</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-[#F5EFE7] hover:text-white border-b border-transparent hover:border-[#D4AF37] pb-1 transition-colors cursor-pointer text-sm">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      {isBookingModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-3xl font-light font-cormorant text-[#2C1810]">Book a Session</h3>
                <button 
                  onClick={() => setIsBookingModalOpen(false)}
                  className="text-[#5C4A42] hover:text-[#2C1810] cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {submitStatus === 'success' ? (
                <div className="text-center py-8">
                  <i className="ri-check-line text-5xl text-green-600 mb-4"></i>
                  <p className="text-lg text-[#2C1810] font-lato">Thank you! Your booking request has been received.</p>
                </div>
              ) : (
                <form id="booking-form" data-readdy-form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-lato text-[#2C1810] mb-2">Service</label>
                    <input 
                      type="text" 
                      name="service"
                      value={selectedService}
                      readOnly
                      className="w-full px-4 py-2 border border-[#D4C4B0] rounded-lg bg-[#F5EFE7] text-sm font-lato"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-lato text-[#2C1810] mb-2">Name *</label>
                    <input 
                      type="text" 
                      name="name"
                      required
                      className="w-full px-4 py-2 border border-[#D4C4B0] rounded-lg focus:outline-none focus:border-[#6B1F3D] text-sm font-lato"
                      placeholder="Your full name"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-lato text-[#2C1810] mb-2">Email *</label>
                    <input 
                      type="email" 
                      name="email"
                      required
                      className="w-full px-4 py-2 border border-[#D4C4B0] rounded-lg focus:outline-none focus:border-[#6B1F3D] text-sm font-lato"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-lato text-[#2C1810] mb-2">Phone</label>
                    <input 
                      type="tel" 
                      name="phone"
                      className="w-full px-4 py-2 border border-[#D4C4B0] rounded-lg focus:outline-none focus:border-[#6B1F3D] text-sm font-lato"
                      placeholder="Your phone number"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-lato text-[#2C1810] mb-2">Preferred Date</label>
                    <input 
                      type="date" 
                      name="preferred_date"
                      className="w-full px-4 py-2 border border-[#D4C4B0] rounded-lg focus:outline-none focus:border-[#6B1F3D] text-sm font-lato"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-lato text-[#2C1810] mb-2">Message</label>
                    <textarea 
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2 border border-[#D4C4B0] rounded-lg focus:outline-none focus:border-[#6B1F3D] text-sm font-lato resize-none"
                      placeholder="Tell me a bit about what you're looking for..."
                    ></textarea>
                    <p className="text-xs text-[#5C4A42] mt-1 font-lato">Maximum 500 characters</p>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600 font-lato">Something went wrong. Please try again.</p>
                    </div>
                  )}

                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#6B1F3D] text-[#F5EFE7] px-6 py-3 rounded-full hover:bg-[#8B2F4D] transition-all whitespace-nowrap cursor-pointer font-lato disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Submit Booking Request'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
